import { useState } from 'react';
import { useAuth } from '@/contexts/AuthContext';
import { getI18n } from '@/lib/i18n';
import api, { formatApiError } from '@/lib/api';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Textarea } from '@/components/ui/textarea';
import { Button } from '@/components/ui/button';
import { toast } from 'sonner';
import { MessageSquare, X, Send, Paperclip, Trash2 } from 'lucide-react';

const MAX_TOTAL_BYTES = 10 * 1024 * 1024; // 10 МБ

function formatSize(n, t) {
  if (n < 1024) return `${n} ${t('att.size.b')}`;
  if (n < 1024 * 1024) return `${(n / 1024).toFixed(1)} ${t('att.size.kb')}`;
  return `${(n / 1024 / 1024).toFixed(1)} ${t('att.size.mb')}`;
}

export default function FeedbackDialog({ open, onClose }) {
  const { user } = useAuth();
  const { t } = getI18n(user);
  const [subject, setSubject] = useState('');
  const [message, setMessage] = useState('');
  const [files, setFiles] = useState([]);
  const [loading, setLoading] = useState(false);

  if (!open) return null;

  const totalSize = files.reduce((s, f) => s + f.size, 0);

  const addFiles = (e) => {
    const list = Array.from(e.target.files || []);
    e.target.value = '';
    if (list.length === 0) return;
    const merged = [...files, ...list];
    const total = merged.reduce((s, f) => s + f.size, 0);
    if (total > MAX_TOTAL_BYTES) {
      return toast.error(t('fb.too_big'));
    }
    setFiles(merged);
  };

  const removeFile = (idx) => setFiles(prev => prev.filter((_, i) => i !== idx));

  const submit = async () => {
    if (!subject.trim() || !message.trim()) {
      return toast.error(t('fb.required'));
    }
    setLoading(true);
    try {
      const fd = new FormData();
      fd.append('subject', subject);
      fd.append('message', message);
      files.forEach(f => fd.append('files', f, f.name));
      await api.post('/feedback', fd, { headers: { 'Content-Type': 'multipart/form-data' } });
      toast.success(t('fb.sent'));
      setSubject(''); setMessage(''); setFiles([]);
      onClose && onClose();
    } catch (err) {
      toast.error(formatApiError(err.response?.data?.detail));
    } finally { setLoading(false); }
  };

  return (
    <div className="fixed inset-0 bg-slate-900/40 z-50 flex items-center justify-center p-6" onClick={onClose}>
      <div className="bg-white border border-slate-200 rounded-sm shadow-2xl w-full max-w-lg p-6 space-y-4"
        onClick={e => e.stopPropagation()} data-testid="feedback-dialog">
        <div className="flex items-center justify-between">
          <h3 className="font-heading text-lg font-semibold text-slate-900 flex items-center gap-2">
            <MessageSquare className="w-5 h-5 text-[#0046AD]" /> {t('fb.title')}
          </h3>
          <Button variant="ghost" size="sm" onClick={onClose} data-testid="feedback-close" className="rounded-sm">
            <X className="w-4 h-4" />
          </Button>
        </div>

        <div className="space-y-1.5">
          <Label className="text-sm font-medium text-slate-700">{t('fb.subject')}</Label>
          <Input value={subject} onChange={e => setSubject(e.target.value)}
            placeholder={t('fb.subject.ph')}
            data-testid="feedback-subject" className="border-slate-300 rounded-sm" maxLength={200} />
        </div>

        <div className="space-y-1.5">
          <Label className="text-sm font-medium text-slate-700">{t('fb.message')}</Label>
          <Textarea value={message} onChange={e => setMessage(e.target.value)}
            rows={6} placeholder={t('fb.message.ph')}
            data-testid="feedback-message" className="border-slate-300 rounded-sm" maxLength={10000} />
        </div>

        {/* Attachments */}
        <div className="space-y-1.5">
          <div className="flex items-center justify-between">
            <Label className="text-sm font-medium text-slate-700 flex items-center gap-1.5">
              <Paperclip className="w-3.5 h-3.5" /> {t('fb.attachments')}
              <span className="text-[11px] text-slate-400 font-normal">({t('fb.attachments_hint')})</span>
            </Label>
            <label className="inline-flex items-center gap-1.5 text-xs text-[#0046AD] cursor-pointer hover:underline"
              data-testid="feedback-add-files">
              <Paperclip className="w-3 h-3" /> {t('fb.add_file')}
              <input type="file" className="hidden" multiple onChange={addFiles} />
            </label>
          </div>
          {files.length > 0 && (
            <div className="space-y-1">
              {files.map((f, idx) => (
                <div key={idx} className="flex items-center gap-2 px-2 py-1 bg-slate-50 border border-slate-100 rounded-sm text-xs"
                  data-testid={`feedback-file-${idx}`}>
                  <Paperclip className="w-3 h-3 text-slate-400 shrink-0" />
                  <span className="flex-1 min-w-0 text-slate-700 truncate">{f.name}</span>
                  <span className="text-[10px] text-slate-400 shrink-0">{formatSize(f.size, t)}</span>
                  <button type="button" onClick={() => removeFile(idx)} data-testid={`feedback-file-remove-${idx}`}
                    className="text-rose-500 hover:text-rose-700 shrink-0">
                    <Trash2 className="w-3 h-3" />
                  </button>
                </div>
              ))}
              <div className="text-[10px] text-slate-400 text-right">
                {t('fb.total')}: {formatSize(totalSize, t)} / {formatSize(MAX_TOTAL_BYTES, t)}
              </div>
            </div>
          )}
        </div>

        <div className="flex gap-2 justify-end pt-2">
          <Button variant="outline" onClick={onClose} className="rounded-sm">{t('fb.cancel')}</Button>
          <Button onClick={submit} disabled={loading} data-testid="feedback-submit"
            className="bg-[#0046AD] hover:bg-[#003A8C] rounded-sm font-semibold">
            <Send className="w-4 h-4 mr-1.5" /> {loading ? t('fb.sending') : t('fb.send')}
          </Button>
        </div>
      </div>
    </div>
  );
}
